import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Text,
  VStack,
  Divider,
  Card,
  CardHeader,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Textarea,
  Button,
  HStack,
  useToast,
  Checkbox,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Portal,
  FormControl,
  FormLabel,
  Input,
  Select,
  Stack,
} from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';

const AdminScheduler = () => {
  const { t } = useTranslation();
  const { accessToken: token } = useAuth();
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  const isProductionApi = useMemo(() => apiBaseUrl.includes('overtaxi.es'), [apiBaseUrl]);
  const toast = useToast();
  const [apsLogs, setApsLogs] = useState('');
  const [isLoadingApsLogs, setIsLoadingApsLogs] = useState(false);
  const [apsTasks, setApsTasks] = useState([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [executingTasks, setExecutingTasks] = useState({});
  const [crontabLogs, setCrontabLogs] = useState('');
  const [isLoadingCrontabLogs, setIsLoadingCrontabLogs] = useState(false);
  const [crontabTasks, setCrontabTasks] = useState([]);
  const [isLoadingCrontabTasks, setIsLoadingCrontabTasks] = useState(false);
  const [bootLogs, setBootLogs] = useState('');
  const [isLoadingBootLogs, setIsLoadingBootLogs] = useState(false);
  const [bootTasks, setBootTasks] = useState([]);
  const [, setIsLoadingBootTasks] = useState(false);
  const [showAddTaskForm, setShowAddTaskForm] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [expandedCards, setExpandedCards] = useState({
    aps: false,
    crontab: false,
    boot: false
  });
  const [newTask, setNewTask] = useState({
    nombre_tarea: '',
    descripcion: '',
    funcion_nombre: '',
    tipo_intervalo: 'interval',
    intervalo_minutos: 15,
    cron_expression: '',
    plataforma: 'ambos'
  });
  const loadersRef = useRef({
    handleLoadTasks: null,
    handleLoadApsLogs: null,
    handleLoadCrontabTasks: null,
    handleLoadCrontabLogs: null,
    handleLoadBootTasks: null,
    handleLoadBootLogs: null,
  });

  // Función para toggle de cards
  const toggleCard = (cardName) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardName]: !prev[cardName]
    }));
  };

  // Función para humanizar expresiones cron
  const humanizeCron = (cronExpr) => {
    if (!cronExpr) return '';
    
    const parts = cronExpr.trim().split(/\s+/);
    if (parts.length !== 5) return cronExpr;
    
    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
    
    // Cada X minutos
    if (minute.startsWith('*/')) {
      const mins = minute.substring(2);
      return `Cada ${mins} minutos`;
    }
    
    // Cada X horas
    if (hour.startsWith('*/')) {
      const hrs = hour.substring(2);
      return `Cada ${hrs} horas`;
    }
    
    // A una hora específica cada día
    if (minute !== '*' && hour !== '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
      return `Diario a las ${hour}:${minute.padStart(2, '0')}`;
    }
    
    // Casos especiales comunes
    if (cronExpr === '0 0 * * *') return 'Diario a medianoche';
    if (cronExpr === '0 */6 * * *') return 'Cada 6 horas';
    if (cronExpr === '*/15 * * * *') return 'Cada 15 minutos';
    if (cronExpr === '0 * * * *') return 'Cada hora';
    
    return cronExpr;
  };

  // Cargar tareas programadas desde la BD
  const handleLoadTasks = async (silencioso = false) => {
    setIsLoadingTasks(true);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const fullUrl = `${apiBaseUrl}/api/admin/scheduler/tareas`;
      
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.tareas && data.tareas.length > 0) {
        // Mapear tareas para mostrar en la tabla
        const tareasFormateadas = data.tareas.map(tarea => {
          // Formatear intervalo
          let intervalo = '';
          if (tarea.tipo_intervalo === 'interval') {
            intervalo = `Cada ${tarea.intervalo_minutos} min`;
          } else if (tarea.tipo_intervalo === 'cron') {
            intervalo = humanizeCron(tarea.cron_expression);
          }

          // Formatear última ejecución
          let ultimaEjecucion = '-';
          if (tarea.ultima_ejecucion_timestamp) {
            const fecha = new Date(tarea.ultima_ejecucion_timestamp);
            ultimaEjecucion = fecha.toLocaleString('es-ES');
          }

          // Detectar entorno desde BD
          let plataforma = 'Ambos';
          if (tarea.entorno === 'produccion') {
            plataforma = 'Producción';
          } else if (tarea.entorno === 'desarrollo') {
            plataforma = 'Desarrollo';
          } else if (tarea.entorno === 'ambos') {
            plataforma = 'Ambos';
          }

          return {
            id: tarea.id,
            nombre: tarea.nombre_tarea,
            plataforma: plataforma,
            entorno: tarea.entorno,
            descripcion: tarea.descripcion,
            funcion: tarea.funcion_nombre,
            intervalo: intervalo,
            tipo_intervalo: tarea.tipo_intervalo,
            intervalo_minutos: tarea.intervalo_minutos,
            cron_expression: tarea.cron_expression,
            estado: tarea.habilitada ? 'activo' : 'deshabilitado',
            habilitada: tarea.habilitada,
            ultimaEjecucion: ultimaEjecucion,
            exitosa: tarea.ultima_ejecucion_exitosa,
            duracion: tarea.ultima_ejecucion_duracion,
            informe: tarea.ultima_ejecucion_informe,
          };
        });

        setApsTasks(tareasFormateadas);
        
        if (!silencioso) {
          toast({
            title: 'Tareas cargadas',
            description: `${data.total} tareas programadas`,
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
        }
      } else {
        setApsTasks([]);
        if (!silencioso) {
          toast({
            title: 'Información',
            description: 'No hay tareas programadas configuradas',
            status: 'info',
            duration: 2000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error al cargar tareas:', error);
      if (!silencioso) {
        toast({
          title: 'Error',
          description: 'No se pudieron cargar las tareas programadas',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoadingTasks(false);
    }
  };

  // Habilitar/deshabilitar tarea
  const handleToggleTask = async (tareaId, habilitada) => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const fullUrl = `${apiBaseUrl}/api/admin/scheduler/tareas/${tareaId}`;
      
      const response = await fetch(fullUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ habilitada: habilitada }),
      });

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      toast({
        title: habilitada ? 'Tarea habilitada' : 'Tarea deshabilitada',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

      // Recargar tareas
      handleLoadTasks(true);
    } catch (error) {
      console.error('Error al actualizar tarea:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la tarea',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Manejar cambios en el formulario de nueva tarea
  const handleNewTaskChange = (field, value) => {
    setNewTask(prev => ({ ...prev, [field]: value }));
  };

  // Iniciar edición de tarea
  const handleEditTask = (task) => {
    // Si ya está editando esta tarea, cancelar la edición
    if (editingTaskId === task.id) {
      setEditingTaskId(null);
      setEditTask(null);
      return;
    }
    
    // Abrir edición de esta tarea
    setEditingTaskId(task.id);
    setEditTask({
      nombre_tarea: task.nombre,
      descripcion: task.descripcion,
      funcion_nombre: task.funcion,
      tipo_intervalo: task.tipo_intervalo,
      intervalo_minutos: task.intervalo_minutos || 15,
      cron_expression: task.cron_expression || '',
      entorno: task.entorno || 'ambos',
      habilitada: task.habilitada
    });
    setShowAddTaskForm(false); // Cerrar formulario de añadir si está abierto
  };

  // Manejar cambios en el formulario de edición
  const handleEditTaskChange = (field, value) => {
    setEditTask(prev => ({ ...prev, [field]: value }));
  };

  // Guardar cambios de edición
  const handleSaveEdit = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const fullUrl = `${apiBaseUrl}/api/admin/scheduler/tareas/${editingTaskId}`;
      
      const response = await fetch(fullUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          nombre_tarea: editTask.nombre_tarea,
          descripcion: editTask.descripcion || null,
          funcion_nombre: editTask.funcion_nombre,
          tipo_intervalo: editTask.tipo_intervalo,
          intervalo_minutos: editTask.tipo_intervalo === 'interval' ? editTask.intervalo_minutos : null,
          cron_expression: editTask.tipo_intervalo === 'cron' ? editTask.cron_expression : null,
          entorno: editTask.entorno,
          habilitada: editTask.habilitada
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Error HTTP: ${response.status}`);
      }

      toast({
        title: 'Tarea actualizada',
        description: 'Los cambios han sido guardados exitosamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Resetear formulario de edición
      setEditingTaskId(null);
      setEditTask(null);
      
      // Recargar lista de tareas
      await handleLoadTasks(true);
    } catch (error) {
      console.error('Error al editar tarea:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo actualizar la tarea',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // Cancelar edición
  const handleCancelEdit = () => {
    setEditingTaskId(null);
    setEditTask(null);
  };

  // Añadir nueva tarea
  const handleAddTask = async () => {
    // Validaciones básicas
    if (!newTask.nombre_tarea || !newTask.funcion_nombre) {
      toast({
        title: 'Error',
        description: 'Los campos Nombre y Función son obligatorios',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (newTask.tipo_intervalo === 'interval' && (!newTask.intervalo_minutos || newTask.intervalo_minutos <= 0)) {
      toast({
        title: 'Error',
        description: 'El intervalo debe ser mayor a 0',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (newTask.tipo_intervalo === 'cron' && !newTask.cron_expression) {
      toast({
        title: 'Error',
        description: 'La expresión cron es obligatoria',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const fullUrl = `${apiBaseUrl}/api/admin/scheduler/tareas`;
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          nombre_tarea: newTask.nombre_tarea,
          descripcion: newTask.descripcion || null,
          funcion_nombre: newTask.funcion_nombre,
          tipo_intervalo: newTask.tipo_intervalo,
          intervalo_minutos: newTask.tipo_intervalo === 'interval' ? newTask.intervalo_minutos : null,
          cron_expression: newTask.tipo_intervalo === 'cron' ? newTask.cron_expression : null,
          entorno: newTask.plataforma,
          habilitada: true
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Error HTTP: ${response.status}`);
      }

      toast({
        title: 'Tarea añadida',
        description: 'La tarea ha sido creada exitosamente',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Resetear formulario y cerrar
      setNewTask({
        nombre_tarea: '',
        descripcion: '',
        funcion_nombre: '',
        tipo_intervalo: 'interval',
        intervalo_minutos: 15,
        cron_expression: '',
        plataforma: 'ambas'
      });
      setShowAddTaskForm(false);
      
      // Recargar lista de tareas
      await handleLoadTasks(true);
    } catch (error) {
      console.error('Error al añadir tarea:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo añadir la tarea',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // Ejecutar tarea manualmente
  const handleExecuteTask = async (tareaId, tareaNombre, tareaEntorno) => {
    // Detectar entorno actual (producción vs desarrollo)
    const hostname = window.location.hostname;
    const esProduccion = hostname.includes('overtaxi.es') || hostname.includes('www.overtaxi.es');
    // Validar que la tarea sea compatible con el entorno actual
    if (tareaEntorno !== 'ambos') {
      if (tareaEntorno === 'produccion' && !esProduccion) {
        toast({
          title: '⚠️ Tarea de producción',
          description: 'Esta tarea solo puede ejecutarse en el servidor de producción (overtaxi.es)',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      
      if (tareaEntorno === 'desarrollo' && esProduccion) {
        toast({
          title: '⚠️ Tarea de desarrollo',
          description: 'Esta tarea solo puede ejecutarse en el servidor de desarrollo (localhost)',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }

    setExecutingTasks(prev => ({ ...prev, [tareaId]: true }));

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const fullUrl = `${apiBaseUrl}/api/admin/scheduler/tareas/${tareaId}/ejecutar`;
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();

      toast({
        title: data.exitosa ? '✓ Tarea ejecutada' : '✗ Error en ejecución',
        description: data.exitosa 
          ? `Completada en ${data.duracion}s` 
          : data.error,
        status: data.exitosa ? 'success' : 'warning',
        duration: 4000,
        isClosable: true,
      });

      // Recargar tareas para ver última ejecución actualizada
      handleLoadTasks(true);
    } catch (error) {
      console.error('Error al ejecutar tarea:', error);
      toast({
        title: 'Error',
        description: 'No se pudo ejecutar la tarea',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setExecutingTasks(prev => ({ ...prev, [tareaId]: false }));
    }
  };

  const handleLoadApsLogs = async (silencioso = false) => {
    setIsLoadingApsLogs(true);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const fullUrl = `${apiBaseUrl}/api/admin/scheduler/logs`;
      
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          setApsLogs('No hay logs disponibles');
          if (!silencioso) {
            toast({
              title: 'Información',
              description: 'No se encontraron logs del scheduler',
              status: 'info',
              duration: 2000,
              isClosable: true,
            });
          }
          return;
        }
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.logs && data.logs.length > 0) {
        // Los logs ya vienen en orden inverso (más reciente primero) desde el backend
        const logsText = data.logs.map(log => log.linea).join('\n');
        setApsLogs(logsText);
        
        if (!silencioso) {
          toast({
            title: 'Logs cargados',
            description: `${data.total} entradas del scheduler`,
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
        }
      } else {
        setApsLogs('No hay logs del scheduler disponibles');
        if (!silencioso) {
          toast({
            title: 'Información',
            description: 'No hay logs del scheduler todavía',
            status: 'info',
            duration: 2000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error al cargar logs del scheduler:', error);
      setApsLogs(`Error al cargar logs: ${error.message}`);
      if (!silencioso) {
        toast({
          title: 'Error',
          description: 'No se pudieron cargar los logs del scheduler',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoadingApsLogs(false);
    }
  };

  // Cargar tareas de crontab
  const handleLoadCrontabTasks = async (silencioso = false) => {
    setIsLoadingCrontabTasks(true);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const fullUrl = `${apiBaseUrl}/api/admin/scheduler/crontab-tasks`;
      
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          setCrontabTasks([]);
          if (!silencioso) {
            toast({
              title: 'Información',
              description: 'No se encontraron tareas de crontab',
              status: 'info',
              duration: 2000,
              isClosable: true,
            });
          }
          return;
        }
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.tasks && data.tasks.length > 0) {
        setCrontabTasks(data.tasks);
        if (!silencioso) {
          toast({
            title: 'Tareas cargadas',
            description: `${data.tasks.length} tareas de crontab`,
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
        }
      } else {
        setCrontabTasks([]);
        if (!silencioso) {
          toast({
            title: 'Información',
            description: 'No hay tareas de crontab configuradas',
            status: 'info',
            duration: 2000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error al cargar tareas de crontab:', error);
      setCrontabTasks([]);
      if (!silencioso) {
        toast({
          title: 'Error',
          description: 'No se pudieron cargar las tareas de crontab',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoadingCrontabTasks(false);
    }
  };

  const handleLoadCrontabLogs = async (silencioso = false) => {
    setIsLoadingCrontabLogs(true);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const fullUrl = `${apiBaseUrl}/api/admin/scheduler/crontab-logs`;
      
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          setCrontabLogs('No hay logs disponibles');
          if (!silencioso) {
            toast({
              title: 'Información',
              description: 'No se encontraron logs de crontab',
              status: 'info',
              duration: 2000,
              isClosable: true,
            });
          }
          return;
        }
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.logs && data.logs.length > 0) {
        // Los logs ya vienen en orden inverso (más reciente primero) desde el backend
        const logsText = data.logs.map(log => log.linea).join('\n');
        setCrontabLogs(logsText);
        
        if (!silencioso) {
          toast({
            title: 'Logs cargados',
            description: `${data.total} entradas de crontab`,
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
        }
      } else {
        setCrontabLogs('No hay logs de crontab disponibles');
        if (!silencioso) {
          toast({
            title: 'Información',
            description: 'No hay logs de crontab todavía',
            status: 'info',
            duration: 2000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error al cargar logs de crontab:', error);
      setCrontabLogs(`Error al cargar logs: ${error.message}`);
      if (!silencioso) {
        toast({
          title: 'Error',
          description: 'No se pudieron cargar los logs de crontab',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoadingCrontabLogs(false);
    }
  };

  // Cargar tareas de boot
  const handleLoadBootTasks = async (silencioso = false) => {
    setIsLoadingBootTasks(true);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const fullUrl = `${apiBaseUrl}/api/admin/scheduler/boot-tasks`;
      
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          setBootTasks([]);
          if (!silencioso) {
            toast({
              title: 'Información',
              description: 'No se encontraron tareas de boot',
              status: 'info',
              duration: 2000,
              isClosable: true,
            });
          }
          return;
        }
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.tasks && data.tasks.length > 0) {
        setBootTasks(data.tasks);
        if (!silencioso) {
          toast({
            title: 'Tareas cargadas',
            description: `${data.tasks.length} tareas de boot`,
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
        }
      } else {
        setBootTasks([]);
        if (!silencioso) {
          toast({
            title: 'Información',
            description: 'No hay tareas de boot configuradas',
            status: 'info',
            duration: 2000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error al cargar tareas de boot:', error);
      setBootTasks([]);
      if (!silencioso) {
        toast({
          title: 'Error',
          description: 'No se pudieron cargar las tareas de boot',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoadingBootTasks(false);
    }
  };

  // Ejecutar tarea cron manualmente
  const handleEjecutarTareaCron = async (task) => {
    const comando = task.comando || task.tarea;
    const comandoCompleto = task.comando_completo || comando;

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const fullUrl = `${apiBaseUrl}/api/admin/scheduler/crontab-tasks/ejecutar`;
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          comando: comando,
          comando_completo: comandoCompleto,
          descripcion: task.descripcion
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || `Error HTTP: ${response.status}`);
      }

      toast({
        title: data.exitosa ? 'Tarea ejecutada' : 'Tarea ejecutada con errores',
        description: `Duración: ${data.duracion}s. ${data.email_enviado ? 'Email enviado.' : ''}`,
        status: data.exitosa ? 'success' : 'warning',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error al ejecutar tarea cron:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo ejecutar la tarea cron',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // Ejecutar tarea boot manualmente
  const handleEjecutarTareaBoot = async (task) => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const fullUrl = `${apiBaseUrl}/api/admin/scheduler/boot-tasks/ejecutar`;
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          servicio: task.tarea,
          accion: 'restart',
          descripcion: task.descripcion
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || `Error HTTP: ${response.status}`);
      }

      toast({
        title: data.exitosa ? 'Servicio reiniciado' : 'Servicio reiniciado con errores',
        description: `Duración: ${data.duracion}s. ${data.email_enviado ? 'Email enviado.' : ''}`,
        status: data.exitosa ? 'success' : 'warning',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error al ejecutar servicio boot:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo ejecutar el servicio',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleLoadBootLogs = async (silencioso = false) => {
    setIsLoadingBootLogs(true);
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const fullUrl = `${apiBaseUrl}/api/admin/scheduler/boot-logs`;
      
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          setBootLogs('No hay logs disponibles');
          if (!silencioso) {
            toast({
              title: 'Información',
              description: 'No se encontraron logs de boot',
              status: 'info',
              duration: 2000,
              isClosable: true,
            });
          }
          return;
        }
        throw new Error(`Error HTTP: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.logs && data.logs.length > 0) {
        const logsText = data.logs.map(log => log.linea).join('\n');
        setBootLogs(logsText);
        
        if (!silencioso) {
          toast({
            title: 'Logs cargados',
            description: `${data.total} entradas de boot`,
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
        }
      } else {
        setBootLogs('No hay logs de boot disponibles');
        if (!silencioso) {
          toast({
            title: 'Información',
            description: 'No hay logs de boot todavía',
            status: 'info',
            duration: 2000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error al cargar logs de boot:', error);
      setBootLogs(`Error al cargar logs: ${error.message}`);
      if (!silencioso) {
        toast({
          title: 'Error',
          description: 'No se pudieron cargar los logs de boot',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoadingBootLogs(false);
    }
  };

  loadersRef.current = {
    handleLoadTasks,
    handleLoadApsLogs,
    handleLoadCrontabTasks,
    handleLoadCrontabLogs,
    handleLoadBootTasks,
    handleLoadBootLogs,
  };

  // Auto-refresh cada minuto
  useEffect(() => {
    const {
      handleLoadTasks: loadTasks,
      handleLoadApsLogs: loadApsLogs,
      handleLoadCrontabTasks: loadCrontabTasks,
      handleLoadCrontabLogs: loadCrontabLogs,
      handleLoadBootTasks: loadBootTasks,
      handleLoadBootLogs: loadBootLogs,
    } = loadersRef.current;

    // Cargar tareas y logs al montar el componente
    loadTasks?.(true);
    loadApsLogs?.(true);
    loadCrontabTasks?.(true);
    loadCrontabLogs?.(true);
    loadBootTasks?.(true);
    loadBootLogs?.(true);

    // Configurar intervalo para refrescar cada minuto (60000ms)
    const interval = setInterval(() => {
      loadTasks?.(true);
      loadApsLogs?.(true);
      loadCrontabTasks?.(true);
      loadCrontabLogs?.(true);
      loadBootTasks?.(true);
      loadBootLogs?.(true);
    }, 60000);

    // Limpiar intervalo al desmontar
    return () => clearInterval(interval);
  }, [token]); // Dependencia: token

  return (
    <VStack spacing={4} align="stretch" p={4} bg="black" color="white" minH="100vh" className="admin-page-container">
      <Box display="flex" justifyContent="space-between" alignItems="center" className="admin-page-header">
          <Text fontSize="xl" fontWeight="bold" className="admin-page-title">
            {t('menu_admin_tareas_programadas')} ({t('admin_scheduler_env_label')} {isProductionApi ? t('admin_scheduler_env_prod') : t('admin_scheduler_env_dev')})
          </Text>
        </Box>
        
        {/* APS - Python Scheduler */}
        <Card variant="outline" borderColor="gray.700" bg="white">
          <CardHeader 
            bg="green.100" 
            color="gray.800" 
            borderTopRadius="md"
            cursor="pointer"
            onClick={() => toggleCard('aps')}
            _hover={{ bg: 'green.200' }}
          >
            <HStack justify="space-between">
              <HStack spacing={2}>
                <Tooltip 
                  label={
                    <VStack align="start" spacing={1} fontSize="xs">
                      <Text fontWeight="bold">📋 TAREAS APS (APScheduler):</Text>
                      <Text>• Gestor de tareas programadas en Python</Text>
                      <Text>• Almacenadas en base de datos PostgreSQL</Text>
                      <Text>• Tipos: interval (cada X minutos) o cron</Text>
                      <Text>• Configurables por entorno: desarrollo/producción/ambos</Text>
                      <Text>• Se pueden habilitar/deshabilitar dinámicamente</Text>
                      <Text>• Ejecutables manualmente desde la interfaz</Text>
                      <Text>• Registra última ejecución, duración y resultado</Text>
                      <Text fontWeight="bold" mt={2}>📄 LOGS APS:</Text>
                      <Text>• Archivo: /var/log/nginx/pyapp_access.log</Text>
                      <Text>• Windows: logs/pyapp/pyapp_access.log</Text>
                      <Text>• Filtra líneas relacionadas con scheduler</Text>
                      <Text>• Incluye: inicio/fin de tareas, errores, notificaciones</Text>
                      <Text>• Últimas 100 líneas, más reciente primero</Text>
                      <Text>• Formato: [PID] timestamp - nivel - mensaje</Text>
                    </VStack>
                  }
                  placement="right"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  p={3}
                >
                  <Text fontSize="lg" cursor="help">❓</Text>
                </Tooltip>
                <Text fontSize="lg" fontWeight="bold">APS - Python Scheduler (tareas de aplicación)</Text>
              </HStack>
              <Text fontSize="2xl">{expandedCards.aps ? '▲' : '▼'}</Text>
            </HStack>
          </CardHeader>
          {expandedCards.aps && (
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <Text color="gray.700">
                {t('admin_scheduler_aps_descripcion')}
              </Text>

              {/* Botones de acción */}
              <HStack justify="flex-end" spacing={2}>
                <Button 
                  size="sm" 
                  colorScheme="green" 
                  onClick={() => setShowAddTaskForm(!showAddTaskForm)}
                  className="admin-button-success"
                >
                  {showAddTaskForm ? 'Cancelar' : 'Añadir tarea'}
                </Button>
              </HStack>

              {/* Formulario para añadir tarea */}
              {showAddTaskForm && (
                <Box borderWidth="1px" borderRadius="lg" p={4} bg="white">
                  <Stack spacing={3}>
                    <HStack spacing={4}>
                      <FormControl isRequired>
                        <FormLabel fontSize="sm" color="gray.700">Nombre de la tarea</FormLabel>
                        <Input 
                          size="sm"
                          value={newTask.nombre_tarea}
                          onChange={(e) => handleNewTaskChange('nombre_tarea', e.target.value)}
                          placeholder="ej: Procesar notificaciones"
                          bg="white"
                          color="gray.800"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm" color="gray.700">Entorno</FormLabel>
                        <Select 
                          size="sm"
                          value={newTask.plataforma}
                          onChange={(e) => handleNewTaskChange('plataforma', e.target.value)}
                          bg="white"
                          color="gray.800"
                        >
                          <option value="ambos">Ambos</option>
                          <option value="produccion">Producción</option>
                          <option value="desarrollo">Desarrollo</option>
                        </Select>
                      </FormControl>
                    </HStack>
                    <FormControl>
                      <FormLabel fontSize="sm" color="gray.700">Descripción</FormLabel>
                      <Input 
                        size="sm"
                        value={newTask.descripcion}
                        onChange={(e) => handleNewTaskChange('descripcion', e.target.value)}
                        placeholder="Descripción de la tarea"
                        bg="white"
                        color="gray.800"
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" color="gray.700">Función</FormLabel>
                      <Input 
                        size="sm"
                        value={newTask.funcion_nombre}
                        onChange={(e) => handleNewTaskChange('funcion_nombre', e.target.value)}
                        placeholder="ej: procesar_notificaciones_email"
                        fontFamily="monospace"
                        bg="white"
                        color="gray.800"
                      />
                    </FormControl>
                    <HStack spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm" color="gray.700">Tipo de intervalo</FormLabel>
                        <Select 
                          size="sm"
                          value={newTask.tipo_intervalo}
                          onChange={(e) => handleNewTaskChange('tipo_intervalo', e.target.value)}
                          bg="white"
                          color="gray.800"
                        >
                          <option value="interval">Intervalo (minutos)</option>
                          <option value="cron">Cron</option>
                        </Select>
                      </FormControl>
                      {newTask.tipo_intervalo === 'interval' ? (
                        <FormControl isRequired>
                          <FormLabel fontSize="sm" color="gray.700">Intervalo (minutos)</FormLabel>
                          <Input 
                            size="sm"
                            type="number"
                            value={newTask.intervalo_minutos}
                            onChange={(e) => handleNewTaskChange('intervalo_minutos', parseInt(e.target.value))}
                            min="1"
                            bg="white"
                            color="gray.800"
                          />
                        </FormControl>
                      ) : (
                        <FormControl isRequired>
                          <FormLabel fontSize="sm" color="gray.700">Expresión Cron</FormLabel>
                          <Input 
                            size="sm"
                            value={newTask.cron_expression}
                            onChange={(e) => handleNewTaskChange('cron_expression', e.target.value)}
                            placeholder="ej: 0 */6 * * *"
                            fontFamily="monospace"
                            bg="white"
                            color="gray.800"
                          />
                        </FormControl>
                      )}
                    </HStack>
                    <HStack justify="flex-end">
                      <Button size="sm" onClick={() => setShowAddTaskForm(false)}>
                        Cancelar
                      </Button>
                      <Button size="sm" colorScheme="green" onClick={handleAddTask}>
                        Guardar tarea
                      </Button>
                    </HStack>
                  </Stack>
                </Box>
              )}

              {/* Tabla de tareas */}
              <Box overflowX="auto" borderWidth="1px" borderRadius="lg" p={4} className="admin-table-container">
                {isLoadingTasks ? (
                  <Text textAlign="center" color="gray.500">Cargando tareas...</Text>
                ) : apsTasks.length === 0 ? (
                  <Text textAlign="center" color="gray.500">No hay tareas programadas configuradas</Text>
                ) : (
                  <Table variant="simple" size="sm" className="admin-table">
                    <Thead>
                      <Tr>
                        <Th maxW="60px">Editar</Th>
                        <Th maxW="120px">Tarea</Th>
                        <Th maxW="80px">Entorno</Th>
                        <Th maxW="150px">Descripción</Th>
                        <Th maxW="150px">Función</Th>
                        <Th maxW="100px">Intervalo</Th>
                        <Th maxW="80px" textAlign="center">Habilitada</Th>
                        <Th maxW="120px">Última ejecución</Th>
                        <Th maxW="80px">Resultado</Th>
                        <Th maxW="80px" whiteSpace="normal" lineHeight="1.2">Ejecutar ahora</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {apsTasks.map((task, index) => (
                        <React.Fragment key={task.id || index}>
                          <Tr>
                            <Td maxW="60px" textAlign="center">
                              <Button
                                size="xs"
                                colorScheme="blue"
                                variant="ghost"
                                onClick={() => handleEditTask(task)}
                                aria-label="Editar tarea"
                              >
                                ✏️
                              </Button>
                            </Td>
                            <Td maxW="120px" fontWeight="medium" whiteSpace="normal" color="gray.800">{task.nombre}</Td>
                            <Td maxW="80px">
                              <Badge colorScheme="purple" fontSize="xs" variant="solid">{task.plataforma}</Badge>
                            </Td>
                            <Td maxW="150px" fontSize="sm" color="gray.600" whiteSpace="normal">{task.descripcion}</Td>
                            <Td maxW="150px" fontSize="xs" fontFamily="monospace" color="blue.600" whiteSpace="normal" wordBreak="break-all">
                              {task.funcion}
                            </Td>
                            <Td maxW="100px" whiteSpace="normal">
                              <Badge colorScheme="blue" fontSize="xs" variant="solid">{task.intervalo}</Badge>
                            </Td>
                            <Td maxW="80px" textAlign="center">
                              <Checkbox 
                                isChecked={task.habilitada}
                                onChange={(e) => handleToggleTask(task.id, e.target.checked)}
                                colorScheme="green"
                              />
                            </Td>
                            <Td maxW="120px" fontSize="xs" color="gray.500" whiteSpace="normal">
                              {task.informe ? (
                                <Popover 
                                  trigger="hover" 
                                  placement="left"
                                  isLazy 
                                  lazyBehavior="unmount"
                                  closeOnBlur={true}
                                >
                                  <PopoverTrigger>
                                    <Box cursor="help" display="inline-block">
                                      {task.ultimaEjecucion}
                                      {task.duracion && <Text fontSize="xs" color="gray.400">({task.duracion.toFixed(2)}s)</Text>}
                                    </Box>
                                  </PopoverTrigger>
                                  <Portal>
                                    <PopoverContent
                                      maxW="650px"
                                      maxH="500px"
                                      bg="gray.800"
                                      color="white"
                                      borderColor="gray.600"
                                      boxShadow="2xl"
                                      zIndex={10000}
                                    >
                                      <PopoverArrow bg="gray.800" />
                                      <PopoverBody
                                        overflowY="auto"
                                        overflowX="hidden"
                                        p={0}
                                        css={{
                                          '&::-webkit-scrollbar': {
                                            width: '8px'
                                          },
                                          '&::-webkit-scrollbar-thumb': {
                                            background: '#4A5568',
                                            borderRadius: '4px'
                                          }
                                        }}
                                        dangerouslySetInnerHTML={{ __html: task.informe }}
                                      />
                                    </PopoverContent>
                                  </Portal>
                                </Popover>
                              ) : (
                                <Box>
                                  {task.ultimaEjecucion}
                                  {task.duracion && <Text fontSize="xs" color="gray.400">({task.duracion.toFixed(2)}s)</Text>}
                                </Box>
                              )}
                            </Td>
                            <Td maxW="80px">
                              {task.exitosa === true && <Badge colorScheme="green" fontSize="xs" variant="solid">✓ Éxito</Badge>}
                              {task.exitosa === false && <Badge colorScheme="red" fontSize="xs" variant="solid">✗ Error</Badge>}
                              {task.exitosa === null && <Badge colorScheme="gray" fontSize="xs" variant="solid">-</Badge>}
                            </Td>
                            <Td maxW="80px" textAlign="center">
                              <Tooltip label="Ejecutar tarea ahora" placement="top">
                                <Button
                                  size="sm"
                                  colorScheme="purple"
                                  variant="ghost"
                                  onClick={() => handleExecuteTask(task.id, task.nombre, task.entorno)}
                                  isLoading={executingTasks[task.id]}
                                  aria-label="Ejecutar tarea"
                                >
                                  ▶️
                                </Button>
                              </Tooltip>
                            </Td>
                          </Tr>
                          
                          {/* Formulario de edición justo debajo de la tarea */}
                          {editingTaskId === task.id && editTask && (
                            <Tr>
                              <Td colSpan={10} p={0}>
                                <Box borderWidth="1px" borderRadius="lg" p={4} bg="gray.50" m={2}>
                                  <Text fontSize="md" fontWeight="bold" mb={3} color="gray.800">Editar tarea</Text>
                                  <Stack spacing={3}>
                                    <HStack spacing={4}>
                                      <FormControl isRequired>
                                        <FormLabel fontSize="sm" color="gray.700">Nombre de la tarea</FormLabel>
                                        <Input 
                                          size="sm"
                                          value={editTask.nombre_tarea}
                                          onChange={(e) => handleEditTaskChange('nombre_tarea', e.target.value)}
                                          bg="white"
                                          color="gray.800"
                                        />
                                      </FormControl>
                                      <FormControl>
                                        <FormLabel fontSize="sm" color="gray.700">Entorno</FormLabel>
                                        <Select 
                                          size="sm"
                                          value={editTask.entorno}
                                          onChange={(e) => handleEditTaskChange('entorno', e.target.value)}
                                          bg="white"
                                          color="gray.800"
                                        >
                                          <option value="ambos">Ambos</option>
                                          <option value="produccion">Producción</option>
                                          <option value="desarrollo">Desarrollo</option>
                                        </Select>
                                      </FormControl>
                                    </HStack>
                                    <FormControl>
                                      <FormLabel fontSize="sm" color="gray.700">Descripción</FormLabel>
                                      <Input 
                                        size="sm"
                                        value={editTask.descripcion}
                                        onChange={(e) => handleEditTaskChange('descripcion', e.target.value)}
                                        bg="white"
                                        color="gray.800"
                                      />
                                    </FormControl>
                                    <FormControl isRequired>
                                      <FormLabel fontSize="sm" color="gray.700">Función</FormLabel>
                                      <Input 
                                        size="sm"
                                        value={editTask.funcion_nombre}
                                        onChange={(e) => handleEditTaskChange('funcion_nombre', e.target.value)}
                                        fontFamily="monospace"
                                        bg="white"
                                        color="gray.800"
                                      />
                                    </FormControl>
                                    <HStack spacing={4}>
                                      <FormControl>
                                        <FormLabel fontSize="sm" color="gray.700">Tipo de intervalo</FormLabel>
                                        <Select 
                                          size="sm"
                                          value={editTask.tipo_intervalo}
                                          onChange={(e) => handleEditTaskChange('tipo_intervalo', e.target.value)}
                                          bg="white"
                                          color="gray.800"
                                        >
                                          <option value="interval">Intervalo (minutos)</option>
                                          <option value="cron">Cron</option>
                                        </Select>
                                      </FormControl>
                                      {editTask.tipo_intervalo === 'interval' ? (
                                        <FormControl isRequired>
                                          <FormLabel fontSize="sm" color="gray.700">Intervalo (minutos)</FormLabel>
                                          <Input 
                                            size="sm"
                                            type="number"
                                            value={editTask.intervalo_minutos}
                                            onChange={(e) => handleEditTaskChange('intervalo_minutos', parseInt(e.target.value))}
                                            min="1"
                                            bg="white"
                                            color="gray.800"
                                          />
                                        </FormControl>
                                      ) : (
                                        <FormControl isRequired>
                                          <FormLabel fontSize="sm" color="gray.700">Expresión Cron</FormLabel>
                                          <Input 
                                            size="sm"
                                            value={editTask.cron_expression}
                                            onChange={(e) => handleEditTaskChange('cron_expression', e.target.value)}
                                            placeholder="ej: 0 */6 * * *"
                                            fontFamily="monospace"
                                            bg="white"
                                            color="gray.800"
                                          />
                                        </FormControl>
                                      )}
                                    </HStack>
                                    <FormControl display="flex" alignItems="center">
                                      <FormLabel fontSize="sm" mb="0" color="gray.700">Habilitada</FormLabel>
                                      <Checkbox 
                                        isChecked={editTask.habilitada}
                                        onChange={(e) => handleEditTaskChange('habilitada', e.target.checked)}
                                        colorScheme="green"
                                      />
                                    </FormControl>
                                    <HStack justify="flex-end">
                                      <Button size="sm" onClick={handleCancelEdit}>
                                        Cancelar
                                      </Button>
                                      <Button size="sm" colorScheme="blue" onClick={handleSaveEdit}>
                                        Guardar cambios
                                      </Button>
                                    </HStack>
                                  </Stack>
                                </Box>
                              </Td>
                            </Tr>
                          )}
                        </React.Fragment>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </Box>

              {/* Logs de APS */}
              <Box className="admin-logs-container">
                <HStack justify="space-between" mb={2} className="admin-logs-header">
                  <Text fontSize="md" fontWeight="semibold" className="admin-logs-title">Logs de ejecución</Text>
                  <Button 
                    size="sm" 
                    colorScheme="green" 
                    onClick={handleLoadApsLogs}
                    isLoading={isLoadingApsLogs}
                    className="admin-button-success"
                  >
                    Refrescar logs
                  </Button>
                </HStack>
                <Textarea
                  value={apsLogs}
                  readOnly
                  placeholder="Los logs aparecerán aquí..."
                  rows={15}
                  className="admin-logs-textarea"
                  fontFamily="monospace"
                  fontSize="xs"
                  bg="gray.900"
                  color="green.300"
                  borderColor="gray.600"
                  _hover={{ borderColor: 'gray.500' }}
                  resize="vertical"
                />
              </Box>

            </VStack>
          </CardBody>
          )}
        </Card>

        <Divider />

        {/* Crontab */}
        <Card variant="outline" borderColor="gray.700" bg="white">
          <CardHeader 
            bg="blue.100" 
            color="gray.800" 
            borderTopRadius="md"
            cursor="pointer"
            onClick={() => toggleCard('crontab')}
            _hover={{ bg: 'blue.200' }}
          >
            <HStack justify="space-between">
              <HStack spacing={2}>
                <Tooltip 
                  label={
                    <VStack align="start" spacing={1} fontSize="xs">
                      <Text fontWeight="bold">📋 TAREAS CRONTAB:</Text>
                      <Text>• Ejecuta: crontab -l (ubuntu) + sudo crontab -l (root)</Text>
                      <Text>• Tareas de usuario [ubuntu]: aplicación</Text>
                      <Text>• Tareas de sistema [root]: mantenimiento</Text>
                      <Text>• Parsea expresiones cron</Text>
                      <Text>• Humaniza intervalos</Text>
                      <Text>• Detecta: backup, certbot, deploy, logs</Text>
                      <Text fontWeight="bold" mt={2}>📄 LOGS CRONTAB:</Text>
                      <Text>• Prioridad 1: journalctl -u cron</Text>
                      <Text>• Prioridad 2: /var/log/syslog</Text>
                      <Text>• Prioridad 3: /var/log/cron</Text>
                      <Text>• Filtra por: cron, backup</Text>
                      <Text>• Últimas 100 líneas</Text>
                    </VStack>
                  }
                  placement="right"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  p={3}
                >
                  <Text fontSize="lg" cursor="help">❓</Text>
                </Tooltip>
                <Text fontSize="lg" fontWeight="bold">Crontab (tareas de sistema)</Text>
              </HStack>
              <Text fontSize="2xl">{expandedCards.crontab ? '▲' : '▼'}</Text>
            </HStack>
          </CardHeader>
          {expandedCards.crontab && (
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <Text color="gray.700">
                {t('admin_scheduler_crontab_descripcion')}
              </Text>

              {/* Tabla de tareas */}
              <Box overflowX="auto" borderWidth="1px" borderRadius="lg" p={4} className="admin-table-container">
                {isLoadingCrontabTasks ? (
                  <Text textAlign="center" color="gray.500" py={4}>Cargando tareas de crontab...</Text>
                ) : (
                  <Table variant="simple" size="sm" className="admin-table">
                    <Thead>
                      <Tr>
                        <Th>Tarea</Th>
                        <Th>Descripción</Th>
                        <Th>Intervalo</Th>
                        <Th>Estado</Th>
                        <Th>Acción</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {crontabTasks.length === 0 ? (
                        <Tr>
                          <Td colSpan={5} textAlign="center" color="gray.500" py={4}>
                            No hay tareas de crontab configuradas
                          </Td>
                        </Tr>
                      ) : (
                        crontabTasks.map((task, index) => (
                          <Tr key={index}>
                            <Td fontFamily="monospace" fontSize="xs" whiteSpace="normal" color="gray.800">{task.tarea}</Td>
                            <Td whiteSpace="normal" color="gray.800">{task.descripcion}</Td>
                            <Td>
                              <Badge colorScheme="blue" fontSize="xs">{task.intervalo}</Badge>
                            </Td>
                            <Td>
                              <Badge 
                                colorScheme={task.estado === 'activo' ? 'green' : 'gray'} 
                                fontSize="xs"
                              >
                                {task.estado === 'activo' ? 'Activo' : 'Inactivo'}
                              </Badge>
                            </Td>
                            <Td>
                              <Button
                                size="xs"
                                colorScheme="blue"
                                onClick={() => handleEjecutarTareaCron(task)}
                                leftIcon={<span>▶</span>}
                              >
                                Ejecutar
                              </Button>
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                )}
              </Box>

              {/* Logs de Crontab */}
              <Box className="admin-logs-container">
                <HStack justify="space-between" mb={2} className="admin-logs-header">
                  <Text fontSize="md" fontWeight="semibold" className="admin-logs-title">Logs de ejecución</Text>
                  <Button 
                    size="sm" 
                    colorScheme="blue" 
                    onClick={handleLoadCrontabLogs}
                    isLoading={isLoadingCrontabLogs}
                  >
                    Refrescar logs
                  </Button>
                </HStack>
                <Textarea
                  value={crontabLogs}
                  readOnly
                  placeholder="Los logs aparecerán aquí..."
                  rows={15}
                  fontFamily="monospace"
                  fontSize="xs"
                  bg="gray.900"
                  color="green.300"
                  borderColor="gray.600"
                  _hover={{ borderColor: 'gray.500' }}
                  resize="vertical"
                />
              </Box>

            </VStack>
          </CardBody>
          )}
        </Card>

        <Divider />

        {/* Boot */}
        <Card variant="outline" borderColor="gray.700" bg="white">
          <CardHeader 
            bg="orange.100" 
            color="gray.800" 
            borderTopRadius="md"
            cursor="pointer"
            onClick={() => toggleCard('boot')}
            _hover={{ bg: 'orange.200' }}
          >
            <HStack justify="space-between">
              <HStack spacing={2}>
                <Tooltip 
                  label={
                    <VStack align="start" spacing={1} fontSize="xs">
                      <Text fontWeight="bold">🚀 TAREAS BOOT:</Text>
                      <Text>• systemctl list-unit-files</Text>
                      <Text>• Filtra: postgres, nginx, redis, docker</Text>
                      <Text>• Filtra: ssh, ufw, fail2ban, certbot</Text>
                      <Text>• Filtra: pyapp, gunicorn, node</Text>
                      <Text>• Obtiene estado: systemctl is-active</Text>
                      <Text>• Obtiene descripción detallada</Text>
                      <Text fontWeight="bold" mt={2}>📄 LOGS BOOT:</Text>
                      <Text>• journalctl -b -n 100</Text>
                      <Text>• Logs desde último arranque</Text>
                      <Text>• Últimas 100 líneas</Text>
                      <Text>• Más reciente primero</Text>
                    </VStack>
                  }
                  placement="right"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  p={3}
                >
                  <Text fontSize="lg" cursor="help">❓</Text>
                </Tooltip>
                <Text fontSize="lg" fontWeight="bold">Boot (tareas de arranque)</Text>
              </HStack>
              <Text fontSize="2xl">{expandedCards.boot ? '▲' : '▼'}</Text>
            </HStack>
          </CardHeader>
          {expandedCards.boot && (
          <CardBody>
            <VStack align="stretch" spacing={4}>
              <Text color="gray.700">
                Servicios y scripts que se ejecutan al iniciar el sistema operativo.
              </Text>

              {/* Tabla de tareas de boot */}
              <Box>
                <Text fontSize="md" fontWeight="semibold" mb={2} color="gray.700">
                  Tareas configuradas
                </Text>
                <Table size="sm" variant="simple">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Servicio</Th>
                      <Th>Descripción</Th>
                      <Th>Tipo</Th>
                      <Th>Estado</Th>
                      <Th>Acción</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {bootTasks.length === 0 ? (
                      <Tr>
                        <Td colSpan={5} textAlign="center" color="gray.500" py={4}>
                          No hay tareas de boot configuradas
                        </Td>
                      </Tr>
                    ) : (
                      bootTasks.map((task, index) => (
                        <Tr key={index}>
                          <Td fontFamily="monospace" fontSize="xs" whiteSpace="normal" color="gray.800">{task.tarea}</Td>
                          <Td whiteSpace="normal" color="gray.800">{task.descripcion}</Td>
                          <Td>
                            <Badge 
                              colorScheme={task.tipo === 'systemd' ? 'purple' : 'blue'} 
                              fontSize="xs"
                            >
                              {task.tipo}
                            </Badge>
                          </Td>
                          <Td>
                            <Badge 
                              colorScheme={
                                task.estado === 'activo' ? 'green' : 
                                task.estado === 'fallido' ? 'red' : 'gray'
                              } 
                              fontSize="xs"
                            >
                              {task.estado}
                            </Badge>
                          </Td>
                          <Td>
                            <Button
                              size="xs"
                              colorScheme="orange"
                              onClick={() => handleEjecutarTareaBoot(task)}
                              leftIcon={<span>🔄</span>}
                            >
                              Reiniciar
                            </Button>
                          </Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>

              {/* Logs de boot */}
              <Box className="admin-logs-container">
                <HStack justify="space-between" align="center" mb={2} className="admin-logs-header">
                  <Text fontSize="md" fontWeight="semibold" color="gray.700" className="admin-logs-title">
                    Logs de arranque
                  </Text>
                  <Button 
                    size="sm" 
                    colorScheme="orange" 
                    onClick={() => handleLoadBootLogs(false)}
                    isLoading={isLoadingBootLogs}
                    className="admin-button-primary"
                  >
                    Refrescar logs
                  </Button>
                </HStack>
                <Textarea
                  value={bootLogs}
                  readOnly
                  rows={15}
                  fontFamily="monospace"
                  fontSize="xs"
                  bg="gray.900"
                  color="green.300"
                  borderColor="gray.600"
                  _hover={{ borderColor: 'gray.500' }}
                  resize="vertical"
                  placeholder="Los logs de arranque aparecerán aquí..."
                  className="admin-logs-textarea"
                />
              </Box>
            </VStack>
          </CardBody>
          )}
        </Card>
    </VStack>
  );
};

export default AdminScheduler;
